import { useState, useEffect } from "react";

interface TypewriterEffectProps {
  text: string;
  speed?: number;
  delay?: number;
  className?: string;
  onComplete?: () => void;
  loop?: boolean;
  pauseDuration?: number;
}

export default function TypewriterEffect({ 
  text, 
  speed = 50, 
  delay = 0, 
  className = "",
  onComplete,
  loop = false,
  pauseDuration = 2000
}: TypewriterEffectProps) {
  const [displayedText, setDisplayedText] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [started, setStarted] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const startTimer = setTimeout(() => {
      setStarted(true);
    }, delay);

    return () => clearTimeout(startTimer);
  }, [delay]);

  useEffect(() => {
    if (!started) return;

    if (isPaused) {
      const pauseTimer = setTimeout(() => {
        setIsPaused(false);
        setIsDeleting(true);
      }, pauseDuration);
      return () => clearTimeout(pauseTimer);
    }

    if (isDeleting) {
      if (displayedText.length > 0) {
        const timer = setTimeout(() => {
          setDisplayedText(prev => prev.slice(0, -1));
        }, speed / 2); // Delete faster than typing
        return () => clearTimeout(timer);
      } else {
        // Reset for next loop
        setIsDeleting(false);
        setCurrentIndex(0);
      }
    } else {
      if (currentIndex < text.length) {
        const timer = setTimeout(() => {
          setDisplayedText(prev => prev + text[currentIndex]);
          setCurrentIndex(prev => prev + 1);
        }, speed);
        return () => clearTimeout(timer);
      } else {
        // Finished typing
        if (loop) {
          setIsPaused(true);
        } else if (onComplete) {
          onComplete();
        }
      }
    }
  }, [currentIndex, text, speed, started, onComplete, isDeleting, displayedText, loop, isPaused, pauseDuration]);

  return (
    <span className={className}>
      {displayedText}
      <span className="animate-pulse">|</span>
    </span>
  );
}